<template>
    <component
        v-model="showAgent"
        :is="currentViewComponent"
    >
        <template #header>
            <div
                class="h-16 flex items-center justify-between pr-6 pl-4 shrink-0 gap-2 border-b border-gray-100 dark:border-gray-755"
                :class="{ 'cursor-grab': isWindowView }"
            >
                <div class="flex items-center">
                    <AiAgentsMenu v-model="currentAgent" />

                    <Beta />
                </div>

                <div class="flex items-center gap-x-4">
                    <DeleteIcon20x20
                        class="text-gray-370 dark:text-gray-200 hover:text-red dark:hover:text-red-720 cursor-pointer transition-colors"
                        v-tippy="getTippyTable('Clear context')"
                        @click.stop="handleClearContext"
                    />

                    <SidebarViewIcon20x20
                        v-if="isWindowView"
                        class="max-mb:hidden text-gray-370 dark:text-gray-200 hover:text-gray-300 dark:hover:text-gray-100 cursor-pointer transition-colors"
                        v-tippy="getTippyTable('Switch to Sidebar')"
                        @click.stop="changeView(AGENT_VIEWS.sidebar)"
                    />

                    <WindowViewIcon20x20
                        v-else
                        class="max-mb:hidden text-gray-370 dark:text-gray-200 hover:text-gray-300 dark:hover:text-gray-100 cursor-pointer transition-colors"
                        v-tippy="getTippyTable('Switch to Window')"
                        @click.stop="changeView(AGENT_VIEWS.window)"
                    />

                    <CrossIcon20x20
                        class="text-gray-370 dark:text-gray-200 hover:text-gray-300 dark:hover:text-gray-100 cursor-pointer transition-colors"
                        v-tippy="getTippyTable('Close')"
                        @click.stop="closeAgent"
                    />
                </div>
            </div>
        </template>

        <div
            ref="messagesContainer"
            class="flex-1 overflow-y-auto p-4 flex flex-col gap-3 select-text custom-scroll"
        >
            <div
                v-if="!messages.length && !isStreaming"
                class="flex-1 flex flex-col items-center justify-center text-center gap-2 relative"
            >
                <AiLogoIcon />

                <div class="text-lgtext-black-300 dark:text-white text-xlg">
                    Hey {{ userName }}, how can I help?
                </div>

                <AiPresetsList
                    :currentAgentType="currentAgent.type"
                    @selectPreset="selectPreset"
                />
            </div>

            <AiChatList
                v-else
                :messages="messages"
                :isStreaming="isStreaming"
                :streamingAnswer="streamingAnswer"
            />

            <ErrorBanner :errorMessage="errorMessage" />
        </div>

        <div class="flex items-end gap-x-4 px-4 pb-7.5 ">
            <AiChatInput
                v-model:prompt="prompt"
                @scrollToBottom="scrollToBottom"
                @send="send"
            />

            <AiChatButton
                :isStreaming="isStreaming"
                :disabled="!prompt.trim()"
                @send="send"
                @stop="stop"
            />
        </div>
    </component>
</template>

<script setup>
import {
    ref, watch, nextTick, onMounted, useTemplateRef, computed,
} from 'vue'

import { useAgentStream } from '@/composables/useAgentStream'

import WindowWrapper from '@/components/ai/wrappers/WindowWrapper.vue'
import SidebarWrapper from '@/components/ai/wrappers/SidebarWrapper.vue'
import AiAgentsMenu from '@/components/ai/menu/AiAgentsMenu.vue'
import Beta from '@/components/beta/Beta.vue'
import DeleteIcon20x20 from '@/components/icons/delete/DeleteIcon20x20.vue'
import SidebarViewIcon20x20 from '@/components/icons/view/SidebarViewIcon20x20.vue'
import WindowViewIcon20x20 from '@/components/icons/view/WindowViewIcon20x20.vue'
import CrossIcon20x20 from '@/components/icons/cross/CrossIcon20x20.vue'
import AiLogoIcon from '@/components/ai/icons/AiLogoIcon.vue'
import AiPresetsList from '@/components/ai/list/AiPresetsList.vue'
import AiChatButton from '@/components/ai/buttons/AiChatButton.vue'
import AiChatInput from '@/components/ai/inputs/AiChatInput.vue'
import ErrorBanner from '@/components/ai/banner/ErrorBanner.vue'
import AiChatList from '@/components/ai/list/AiChatList.vue'

import { getTippyTable } from '@/helpers/tippy-helper'
import {
    getCurrentAiAgent, saveCurrentAiAgent, loadAiConversationFromStorage, saveAiConversationToStorage, clearAiConversationStorage,
} from '@/helpers/app-local-storage-helper'

import { useAppStore } from '@/store/appStore'
import { useAuthorizationStore } from '@/store/authorizationStore'

import { AGENT_VIEWS } from '@/constants/agentViews'

const showAgent = defineModel('modelValue', { type: Boolean, default: false })

const appStore = useAppStore()
const authorizationStore = useAuthorizationStore()

const {
    isStreaming, answer, usage, conversationId, errorMessage, start, stop, clearContext,
} = useAgentStream()

const messagesContainer = useTemplateRef('messagesContainer')

const prompt = ref('')
const messages = ref([])
const streamingAnswer = ref('')

const currentAgent = ref(getCurrentAiAgent())

const userName = computed(() => authorizationStore.user?.first_name)

const currentView = computed({
    get() {
        return appStore.currentAiAgentView
    },
    set(view) {
        appStore.toggleAiAgentView(view)
    },
})

const isWindowView = computed(() => appStore.aiAgentOnWindow)

const currentViewComponent = computed(() => {
    switch (currentView.value) {
    case AGENT_VIEWS.sidebar:
        return SidebarWrapper
    case AGENT_VIEWS.window:
        return WindowWrapper
    }
})

onMounted(() => {
    loadAiConversation()
})

function loadAiConversation() {
    const data = loadAiConversationFromStorage(currentAgent.value.type)

    messages.value = data.messages
    conversationId.value = data.conversationId
}

function selectPreset(text) {
    prompt.value = text
}

function changeView(view) {
    currentView.value = view
}

async function scrollToBottom() {
    await nextTick()

    if (messagesContainer.value) {
        messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight
    }
}

function saveToStorage() {
    saveAiConversationToStorage(messages.value, conversationId.value, currentAgent.value.type)
}

function send() {
    const text = prompt.value.trim()

    if (!text || isStreaming.value) {
        return
    }

    messages.value.push({ role: 'user', content: text })

    saveToStorage()

    prompt.value = ''
    streamingAnswer.value = ''

    start({
        prompt: text,
        url: currentAgent.value.url,
    })
}

function handleClearContext() {
    clearContext()

    messages.value = []
    prompt.value = ''
    streamingAnswer.value = ''

    clearAiConversationStorage(currentAgent.value.type)
}

function closeAgent() {
    showAgent.value = false
}

watch(answer, (val) => {
    streamingAnswer.value = val
    scrollToBottom()
})

watch(isStreaming, (streaming) => {
    if (!streaming && streamingAnswer.value) {
        messages.value.push({
            role: 'assistant',
            content: streamingAnswer.value,
            scriptOpen: false,
            usage: usage.value || null,
        })

        streamingAnswer.value = ''

        saveToStorage()
        scrollToBottom()
    }
})

watch(currentAgent, (agent) => {
    if (agent) {
        saveCurrentAiAgent(agent)
        loadAiConversation()
    }
})
</script>
